import { useState } from 'react'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled, { createGlobalStyle } from 'styled-components'

import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import useLanguage from '@/hooks/use-language'
import Footer from '@/features/W1/Footer'
import AppDownload from '@/features/W2Living/app-download'
import FeatureBlock from '@/features/W2Living/feature-block'
import Floorplans from '@/features/W2Living/floorplans'
import ImagesBlock from '@/features/W2Living/images-block'
import { ReactComponent as Logo } from '@/features/W2Living/images/logo-blk.svg'
import markerIcon from '@/features/W2Living/images/marker_icon.png'
import plan1 from '@/features/W2Living/images/plan_1.jpg'
import plan2 from '@/features/W2Living/images/plan_2.jpg'
import plan3 from '@/features/W2Living/images/plan_3.jpg'
import bbqAmenity from '@/features/W2Living/images/w2_amenity_bbq.png'
import carshareAmenity from '@/features/W2Living/images/w2_amenity_carshare.png'
import conciergeAmenity from '@/features/W2Living/images/w2_amenity_concierge.png'
import coworkingAmenity from '@/features/W2Living/images/w2_amenity_coworking.png'
import evAmenity from '@/features/W2Living/images/w2_amenity_ev.png'
import fitnessAmenity from '@/features/W2Living/images/w2_amenity_fitness.png'
import gardenAmenity from '@/features/W2Living/images/w2_amenity_garden.png'
import lockerAmenity from '@/features/W2Living/images/w2_amenity_locker.png'
import loungeAmenity from '@/features/W2Living/images/w2_amenity_lounge.png'
import parkingAmenity from '@/features/W2Living/images/w2_amenity_parking.png'
import w2AmenityImg from '@/features/W2Living/images/w2_amenityimg.png'
import acFeature from '@/features/W2Living/images/w2_feature_ac.png'
import applianceFeature from '@/features/W2Living/images/w2_feature_appliance.png'
import keylessFeature from '@/features/W2Living/images/w2_feature_keyless.png'
import laundryFeature from '@/features/W2Living/images/w2_feature_laundry.png'
import petFeature from '@/features/W2Living/images/w2_feature_pet.png'
import wifiFeature from '@/features/W2Living/images/w2_feature_wifi.png'
import w2FeatureImg from '@/features/W2Living/images/w2_featureimg.png'
import w2PromontLogo from '@/features/W2Living/images/w2_promont_logo.png'
import w2Cover from '@/features/W2Living/images/w2cover.png'
import WalkScore from '@/features/W2Living/walk-score'
import Head from '@/components/head'
import Map from '@/components/maplibre-map/map'
import Marker from '@/components/maplibre-map/marker'

const Copyright = createGlobalStyle`
  .copyright-icons {
    display: none;
  }

  #copyright-text {
    margin-bottom: 0;
  }
`

const { BASE_URL } = ENV_VARS

const StyledSection = styled.section`
  height: 666px;
  @media (max-width: ${breakpoints.phoneMax}) {
    height: 240px;
  }
`

const MapWrapper = styled.div`
  height: 666px;
  flex: 1;
  @media (max-width: ${breakpoints.phoneMax}) {
    height: 240px;
  }
`

const MarkerWrapper = styled.div`
  position: absolute;
  width: 70px;
  top: -42px;
  left: -19px;
`
const FLOORPLANS = [
  {
    image: plan1,
    plan: '1C',
    bed: '1',
    bath: '1',
    hasDen: false,
    linkId: '14775'
  },

  {
    image: plan2,
    plan: '2C',
    bed: '2',
    bath: '2',
    hasDen: false,
    linkId: '14777'
  },
  {
    image: plan3,
    plan: '3A',
    bed: '3',
    bath: '2',
    hasDen: false,
    linkId: '14789'
  }
]

const COORDS = [49.26764, -123.11311]

const MarkerComponent = () => (
  <MarkerWrapper>
    <Image src={markerIcon} width={70} height={70} alt="" loading="eager" />
  </MarkerWrapper>
)

const MapSection = () => (
  <StyledSection>
    <MapWrapper>
      <Map center={COORDS} zoom={15}>
        <Marker position={COORDS} IconComponent={MarkerComponent} />
      </Map>
    </MapWrapper>
  </StyledSection>
)

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 40px;
  position: fixed;
  background-color: ${colors.white};
  width: 100%;
  z-index: 10;
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 10px 15px;
  }
`
const LanguageLink = styled.a`
  color: ${colors.regular};
  padding: 7px 20px;
  cursor: pointer;
  text-decoration: none;

  :hover {
    color: rgba(48, 48, 48, 0.3);
  }
`
const Wrapper = styled.div`
  font-family: 'Open Sans', sans-serif;
`
const StyledLogo = styled(Logo)`
  width: 80px;
`

const StyledHeaderLogo = styled(StyledLogo)`
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 45px;
  }
`

const Banner = styled.div`
  text-align: center;
  background: url(${({ backgroundImg }) => backgroundImg}) 50% 100% no-repeat;
  background-size: cover;
  margin-bottom: 30px;
  height: 750px;
  overflow: hidden;
  position: relative;
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    height: 600px;
  }
`

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 0 20px;
  display: flex;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 60px 25px;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1;
  max-width: 480px;
`

const TitleWrapper = styled.h1`
  padding: 10px 20px 5px;
  width: 100%;
  color: ${colors.white};
  font-family: 'Open Sans', sans-serif;
  font-size: 45px;
  font-weight: ${fontWeights.medium};
  line-height: 60px;
  text-shadow: 0px 3px 3px ${colors.black};
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 26px;
    line-height: 30px;
  }
`

const AddressWrapper = styled.h2`
  padding: 5px 20px;
  width: 100%;
  color: ${colors.white};
  font-family: 'Open Sans', sans-serif;
  font-size: 24px;
  font-weight: ${fontWeights.medium};
  line-height: 60px;
  text-shadow: 0px 3px 3px ${colors.black};
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    line-height: 30px;
  }
`

const StyledButton = styled.button`
  width: 210px;
  color: ${colors.white};
  font-size: 16px;
  height: 48px;
  padding: 0 25px;
  background-color: #445e54;
  font-weight: ${fontWeights.regular};
  box-shadow: 0 3px 3px ${colors.black};
  border-width: 1px;
  &:hover {
    opacity: 0.9;
  }
`

const W2LogoWrapper = styled.div`
  position: absolute;
  bottom: -4px;
  right: 0;
  width: 300px;
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    width: 127px;
  }
`

const BodyTextContainer = styled.div`
  margin-top: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 20px;
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    margin-top: 70px;
  }
`

const BodyTitle = styled.p`
  font-size: 36px;
  width: 100%;
  font-weight: ${fontWeights.regular};
  color: ${colors.black};
  text-align: center;
  margin-bottom: 30px;
  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 24px;
  }
`

const BodyDescription = styled(BodyTitle)`
  font-size: ${fontSizes.regular};
  max-width: 860px;
  margin-bottom: 20px;
  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: ${fontSizes.regular};
  }
`

const FloorplanTitle = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 85%;
  height: 70px;
  background-color: #f6f9f8;
  font-size: 24px;
  font-weight: ${fontWeights.medium};
  color: #445e54;
  margin: 60px auto 20px;
  max-width: 1440px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    width: 100%;
  }
`

const TitleText = styled.p`
  font-size: 24px;
  margin-bottom: 10px;
`

const BottomAddressWrapper = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  height: 92px;
  background-color: #d0dfd4;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: 0px;
  @media (max-width: ${breakpoints.tabletLandscape}) {
    flex-direction: column;
  }
`

const BottomAddressText = styled(TitleText)`
  padding: 0 5px;
  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
  }
`

const ViewAllContainer = styled.div`
  position: relative;
  justify-content: flex-end;
  flex-direction: column;
  width: auto;
  margin: 0 auto;
  max-width: 1440px;
  justify-content: center;
  height: 260px;
  display: ${props => (props.expanded ? 'none' : 'flex')};
  @media (min-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`
const ViewAllButton = styled.button`
  background: ${colors.white};
  width: 230px;
  font-weight: 700;
  font-size: 16px;
  height: 48px;
  padding: 0 25px;
  margin: 0 auto;
  border: 1px solid #445e54;
  color: #445e54;
  z-index: 2;
  &:hover {
    opacity: 0.9;
  }
`

const ViewAllOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  top: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0),
    5%,
    rgba(255, 255, 255, 1)
  );
`

const W2living = () => {
  const { t } = useTranslation('w2living')
  const router = useRouter()
  const { language, changeLanguage } = useLanguage()
  const isEnglish = language.value === 'en'

  const [isFloorplansExpanded, setIsFloorplansExpanded] = useState(false)
  const FEATURES = [
    { text: t('w2living:1 Gbps Internet Included'), image: wifiFeature },
    { text: t('w2living:In-Suite Laundry'), image: laundryFeature },
    { text: t('w2living:Pet Friendly'), image: petFeature },
    { text: t('w2living:High-End Appliances'), image: applianceFeature },
    { text: t('w2living:Air Conditioning'), image: acFeature },
    { text: t('w2living:Keyless Entry'), image: keylessFeature }
  ]

  const AMENITIES = [
    {
      text: t('w2living:24/7 Concierge & Secure Residents’ Portal'),
      image: conciergeAmenity
    },
    { text: t('w2living:Fitness Centre'), image: fitnessAmenity },
    { text: t(`w2living:Resident's Lounge`), image: loungeAmenity },
    { text: t('w2living:Car Share Equipped'), image: carshareAmenity },
    { text: t('w2living:Community Garden'), image: gardenAmenity },
    {
      text: t('w2living:Underground Parking + Gated Access'),
      image: parkingAmenity
    },
    { text: t('w2living:Outdoor BBQ'), image: bbqAmenity },
    { text: t('w2living:EV Ready'), image: evAmenity },
    { text: t('w2living:Coworking Space'), image: coworkingAmenity },
    { text: t('w2living:Snaile Smart Lockers'), image: lockerAmenity }
  ]

  const metaImageUrl = `${BASE_URL}/images/w2-apartments-for-rent-vancouver-canada.png`

  const metaTitle = t('w2living:page_title')
  const metaDescription = t('w2living:page_description')

  return (
    <Wrapper>
      <Head
        hideCustomerSupport
        title={metaTitle}
        description={metaDescription}
        meta={{
          image: metaImageUrl,
          imageAlt: t('w2living:meta_image_alt')
        }}
        path="/w2living"
      />
      <Copyright />
      <Header>
        <Link
          href="/"
          passHref
          aria-label={t('b.landing.home.label', 'liv.rent Homepage')}
          legacyBehavior
        >
          <StyledHeaderLogo />
        </Link>

        <LanguageLink onClick={() => changeLanguage(isEnglish ? 'zh' : 'en')}>
          {isEnglish ? '中文' : 'EN'}
        </LanguageLink>
      </Header>
      <Banner backgroundImg={w2Cover.src}>
        <Container>
          <ContentContainer>
            <TitleWrapper>
              {t('w2living:W2 Vancouver Apartment Rentals')}
            </TitleWrapper>
            <AddressWrapper>
              1988 Wylie St, Vancouver, BC V5Y 3N6
            </AddressWrapper>
            <StyledButton
              theme="primary"
              width={210}
              height={48}
              radius={2}
              type="button"
              onClick={() =>
                router.push(
                  '/building/4517/w2-living-apartment-building-1988-wylie-st-vancouver-bc'
                )
              }
            >
              {t('w2living:View Available Units')}
            </StyledButton>
          </ContentContainer>
        </Container>
        <W2LogoWrapper>
          <Image
            src={w2PromontLogo}
            width={300}
            height={90}
            placeholder="blur"
            alt="W2 Promont Logo"
          />
        </W2LogoWrapper>
      </Banner>

      <BodyTextContainer>
        <BodyTitle>{t('w2living:About the Building')}</BodyTitle>
        <BodyDescription>
          {t(
            'w2living:body description 1',
            'Located in the thriving community of Olympic Village, W2 at Wylie and West 2nd features premium amenities, 24/7 concierge and the keys to a smarter, more connected way to rent and live. About 10 minutes from the downtown core and a short 4-minute walk to Olympic Village Skytrain Station, this is urban rental living that’s balanced by the vitality of the natural surroundings at False Creek.'
          )}
        </BodyDescription>
        <BodyDescription>
          {t(
            'w2living:body description 2',
            'Designed for optimal efficiency while allowing you to personalize your home, suites come furnished or unfurnished. Available in studio, 1, 2 and 3 bedrooms.'
          )}
        </BodyDescription>
        <ViewAllButton
          theme="light"
          width={230}
          height={48}
          radius={2}
          onClick={() => setIsFloorplansExpanded(true)}
        >
          {t('w2living:View All')}
        </ViewAllButton>
      </BodyTextContainer>
      <FloorplanTitle>{t('w2living:Featured Units')}</FloorplanTitle>
      <Floorplans expanded={isFloorplansExpanded} floorplans={FLOORPLANS} />
      <ViewAllContainer expanded={isFloorplansExpanded}>
        <ViewAllOverlay />
        <ViewAllButton
          theme="light"
          width={230}
          height={48}
          radius={2}
          onClick={() => setIsFloorplansExpanded(true)}
        >
          {t('w2living:View All')}
        </ViewAllButton>
      </ViewAllContainer>
      <FeatureBlock
        title={t('w2living:Included Unit Features')}
        image={w2FeatureImg}
        items={FEATURES}
      />
      <FeatureBlock
        reverse
        large
        title={t('w2living:Building Amenities')}
        image={w2AmenityImg}
        items={AMENITIES}
      />
      <AppDownload />
      <WalkScore />
      <ImagesBlock />
      <BottomAddressWrapper>
        <BottomAddressText>
          {t(
            'w2living:address',
            'Location: 1988 Wylie St, Vancouver, BV V5Y 0N4'
          )}
        </BottomAddressText>
      </BottomAddressWrapper>
      <MapSection />
      <Footer />
    </Wrapper>
  )
}

export const getStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['w2living']))
    }
  }
}

export default W2living
